import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../api/adminApi';
import { UsageTab } from './dashboard/UsageTab';
import { QuestionStatsTab } from './dashboard/QuestionStatsTab';
import { LifecycleFunnelTab } from './dashboard/LifecycleFunnelTab';
import { ThemeStatsTab } from './dashboard/ThemeStatsTab';
import { QuestionSourcesTab } from './dashboard/QuestionSourcesTab';
import { RetentionTab } from './dashboard/RetentionTab';
import { TeacherStatsTab } from './teacher-stats/TeacherStats';
import { CostsTab } from './dashboard/CostsTab';
import './Dashboard.css';
import { TabStrip } from '@shared-ui/components';

type TimeRange = '7d' | '4w' | '8w' | 'all';
/** Which users every chart on this page counts. 'prod' = real users only —
 *  dummies, guests and non-prod rings excluded. See statsAudience.ts. */
type Audience = 'prod' | 'all';
const DASHBOARD_TABS = ['usage', 'retention', 'questions', 'questionSources', 'lifecycle', 'theme', 'teacherStats', 'costs'] as const;
type DashboardTab = typeof DASHBOARD_TABS[number];

const ALL_START_DATE = '2026-03-01';

function daysSince(dateStr: string): number {
  const start = new Date(dateStr + 'T00:00:00Z');
  const now = new Date();
  return Math.max(1, Math.floor((now.getTime() - start.getTime()) / 86400000) + 1);
}

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string; days: () => number }[] = [
  { value: '7d', label: '7 Days', days: () => 7 },
  { value: '4w', label: '4 Weeks', days: () => 28 },
  { value: '8w', label: '8 Weeks', days: () => 56 },
  { value: 'all', label: 'All', days: () => daysSince(ALL_START_DATE) },
];

export function Dashboard() {
  // `?tab=` opens a tab directly — how a published link's page links to its funnel.
  const [params] = useSearchParams();
  const [tab, setTab] = useState<DashboardTab>(() => {
    const asked = params.get('tab');
    return asked && DASHBOARD_TABS.includes(asked as DashboardTab) ? asked as DashboardTab : 'usage';
  });
  const [timeRange, setTimeRange] = useState<TimeRange>('4w');
  const [audience, setAudience] = useState<Audience>('prod');
  const includeAll = audience === 'all';
  const rangeDays = TIME_RANGE_OPTIONS.find(o => o.value === timeRange)!.days();
  // Day grain wherever a day's worth of bars is readable — 8 Weeks is 56 of
  // them, which is dense but legible, and the point of the chart is to see
  // individual days. Only `All` buckets by week: it starts at ALL_START_DATE
  // and grows by one bar a day, so it would be unreadable within a term.
  const isWeekly = rangeDays > 56;
  const rangeLabel = TIME_RANGE_OPTIONS.find(o => o.value === timeRange)!.label;

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['stats', rangeDays, audience],
    queryFn: () => adminApi.getStats(rangeDays, includeAll),
    enabled: tab === 'usage',
  });

  const [qsStatus, setQsStatus] = useState<string>('');
  const { data: questionStats, isLoading: qsLoading } = useQuery({
    queryKey: ['questionStats', qsStatus],
    queryFn: () => adminApi.getQuestionStats(qsStatus || undefined),
    enabled: tab === 'questions',
  });
  const { data: questionTimeseries, isLoading: qtLoading } = useQuery({
    queryKey: ['questionStatsTimeseries', qsStatus, rangeDays],
    queryFn: () => adminApi.getQuestionStatsTimeseries({
      status: qsStatus || undefined,
      days: rangeDays,
    }),
    enabled: tab === 'questions',
  });

  const { data: retention, isLoading: retentionLoading } = useQuery({
    queryKey: ['retentionStats', rangeDays, audience],
    queryFn: () => adminApi.getRetentionStats(rangeDays, includeAll),
    enabled: tab === 'retention',
  });

  const { data: themeStats, isLoading: themeLoading } = useQuery({
    queryKey: ['themeStats', rangeDays, audience],
    queryFn: () => adminApi.getThemeStats(rangeDays, includeAll),
    enabled: tab === 'theme',
  });

  // Tabs that use the shared day-range time filter (rendered below the tab line).
  const usesTimeRange = tab === 'usage' || tab === 'retention' || tab === 'questions' || tab === 'questionSources' || tab === 'theme';
  // Tabs whose numbers are per-user, so the audience toggle changes them.
  // Retention is DAU/WAU over real users, so it belongs here too. Question
  // Stats counts questions, not people — a toggle there would imply an effect
  // it cannot have.
  const usesAudience = tab === 'usage' || tab === 'retention' || tab === 'questionSources' || tab === 'theme' || tab === 'teacherStats' || tab === 'costs';

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
      </div>

      <TabStrip
        ariaLabel="Dashboard views"
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'usage', label: 'Usage' },
          { key: 'retention', label: 'Retention' },
          { key: 'questions', label: 'Question Stats' },
          { key: 'questionSources', label: 'Solve Sources' },
          { key: 'lifecycle', label: 'Lifecycle Funnel' },
          { key: 'theme', label: 'Theme' },
          { key: 'teacherStats', label: 'Teacher Stats' },
          { key: 'costs', label: 'Costs' },
        ] as const}
      />

      {/* Controls sit below the tab line, consistent across tabs. */}
      {(usesTimeRange || usesAudience) && (
        <div className="dashboard-controls">
          {tab === 'questions' && (
            <div className="dashboard-time-toggle">
              {[
                { value: '', label: 'All' },
                { value: 'live', label: 'Live' },
                { value: 'draft', label: 'Draft' },
              ].map(opt => (
                <button
                  key={opt.value}
                  className={`time-toggle-btn ${qsStatus === opt.value ? 'active' : ''}`}
                  onClick={() => setQsStatus(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {usesTimeRange && (
          <div className="dashboard-time-toggle">
            {TIME_RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                className={`time-toggle-btn ${timeRange === opt.value ? 'active' : ''}`}
                onClick={() => setTimeRange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          )}
          {/* Audience — applies to every user-scoped chart on the page, so the
              numbers beside each other always describe the same people. */}
          {usesAudience && (
          <div className="dashboard-time-toggle" title="Prod only excludes seeded dummy users, anonymous guests and non-prod rings">
            {([
              { value: 'prod', label: 'Prod only' },
              { value: 'all', label: 'All users' },
            ] as const).map(opt => (
              <button
                key={opt.value}
                className={`time-toggle-btn ${audience === opt.value ? 'active' : ''}`}
                onClick={() => setAudience(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          )}
        </div>
      )}

      {tab === 'usage' && (
        isLoading ? <div className="loading">Loading...</div>
        : error ? <div className="error">Error: {(error as Error).message}</div>
        : stats ? <UsageTab stats={stats} rangeLabel={rangeLabel} isWeekly={isWeekly} />
        : null
      )}

      {tab === 'retention' && (
        <RetentionTab data={retention} isLoading={retentionLoading} rangeLabel={rangeLabel} includeAll={includeAll} />
      )}

      {tab === 'questions' && (
        <QuestionStatsTab
          data={questionStats}
          isLoading={qsLoading}
          timeseries={questionTimeseries?.buckets}
          timeseriesLoading={qtLoading}
          rangeLabel={rangeLabel}
          isWeekly={isWeekly}
          status={qsStatus}
        />
      )}

      {tab === 'questionSources' && <QuestionSourcesTab days={rangeDays} includeAll={includeAll} />}

      {tab === 'lifecycle' && <LifecycleFunnelTab />}

      {tab === 'theme' && (
        <ThemeStatsTab data={themeStats} isLoading={themeLoading} rangeLabel={rangeLabel} />
      )}

      {tab === 'teacherStats' && <TeacherStatsTab includeAll={includeAll} />}

      {tab === 'costs' && <CostsTab includeAll={includeAll} />}
    </div>
  );
}
